import { motion } from 'framer-motion';
import { Brain, Zap, Target } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Abacus3D from '../3d/Abacus3D';

const features = [
  { key: 'abacus.feature1', icon: Brain, color: 'from-purple-500 to-violet-600' },
  { key: 'abacus.feature2', icon: Zap, color: 'from-yellow-500 to-amber-500' },
  { key: 'abacus.feature3', icon: Target, color: 'from-green-500 to-emerald-600' },
];

export default function Abacus() {
  const { t } = useLanguage();

  return (
    <section id="abacus" className="section-padding bg-gradient-to-b from-cp-blue to-cp-blue-dark relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-cp-orange/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-violet-500/20 border border-purple-500/30 mb-6"
            >
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300 font-medium">Brain Development</span>
            </motion.div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('abacus.title')}
            </h2>
            <p className="text-xl text-cp-orange font-semibold mb-6">
              {t('abacus.subtitle')}
            </p>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.key}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4 glass rounded-xl p-4 hover:bg-white/5 transition-colors group"
                  >
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{t(feature.key)}</h3>
                      <p className="text-sm text-gray-400">{t(`${feature.key}.desc`)}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <a
                href="#contact"
                className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white"
              >
                {t('abacus.enroll')}
              </a>
            </motion.div>
          </motion.div>

          {/* 3D Scene */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Abacus3D />
          </motion.div>
        </div>
      </div>
    </section>
  );
}